import { useEffect, useRef } from 'react';
import { ISingleEpletMmRisk } from '@nkr/models/index';
import { Button, OverlayTrigger } from 'react-bootstrap';
import styled from 'styled-components';
import PopoverTemplate from '@nkr/components/popover-template';

const PopoverWrapper = styled(PopoverTemplate)`
    max-width: 600px;
`;

const Risk = {
    NoData: -1,
    Zero: 0,
    Low: 10,
    Intermediate: 20,
    High: 30
};

interface IProps {
    data: ISingleEpletMmRisk,
    centerId?: number,
    isAdmin: boolean,
    showExpectedWaitTimeBlue?: boolean,
    placement?: string,
    width?: string,
    height?: string
}

const SingleEpletMmRisk = (props: IProps) => {
    const triggerRef = useRef<any>(null);

    useEffect(() => {
        const close = () => triggerRef.current?.hide();
        window.addEventListener('scroll', close, { capture: true, passive: true });
        return () => window.removeEventListener('scroll', close, { capture: true } as any);
    }, []);

    if (props.data == null) {
        return (
            <OverlayTrigger
                ref={triggerRef}
                trigger='click'
                rootClose
                overlay={
                    <PopoverTemplate>
                        <div>This donor has not yet been evaluated for the single eplet mismatch risk. This can occur if either the donor or recipient were added today or if they have not yet completed typing.</div>
                    </PopoverTemplate>
                }
            >
                <Button bsStyle={'default'} bsSize='xs' style={{ width: '100%' }}>- ? -</Button>
            </OverlayTrigger>
        );
    }

    let hasData = false;
    let star = null;
    let buttonLabel = 'default';
    let buttonText = '- ? -';

    let singleRisk = '';
    let singleLabel = '';
    let totalRisk = '';
    let totalLabel = '';
    let ambiAppend = '';
    let ambiAlert: JSX.Element = null;
    let waitText: JSX.Element = null;
    let dqRange: JSX.Element = null;

    let { singleEpletMmDq, singleEpletMmDqLow, totalEpletMmDq, totalEpletMmDr, singleEpletRisk, singleEpletRiskLow, totalEpletRisk, overallRisk, isAboCompatible, isCompatiblePair, expectedWaitTimeLowEmm } = props.data;

    if (singleEpletRiskLow < singleEpletRisk) {
        if (singleEpletRiskLow == Risk.Low) {
            ambiAppend = ' ambi-low';
            ambiAlert = <div className='alert alert-danger' style={{ padding: 5 }}>ATTENTION!<br />Please review indeterminate DQA1/DQB1 linkage for accurate eplet MM</div>;
        }

        if (singleEpletRiskLow == Risk.Intermediate) {
            ambiAppend = ' ambi-med';
            ambiAlert = <div className='alert alert-danger' style={{ padding: 5 }}>ATTENTION!<br />Please review indeterminate DQA1/DQB1 linkage for accurate eplet MM</div>;
        }
    }

    if (singleEpletRisk == Risk.Zero) {
        hasData = true;
        singleRisk = '0 MM';
        buttonText = '0 MM';
        buttonLabel = 'success';
        singleLabel = 'success';
    } else if (singleEpletRisk == Risk.Low) {
        hasData = true;
        singleRisk = 'Low';
        buttonText = 'low';
        buttonLabel = 'success';
        singleLabel = 'success';

        if (totalEpletMmDq == 0 && totalEpletMmDr == 0) {
            star = <span className='glyphicon glyphicon-star' style={{ paddingLeft: 5, paddingRight: 5 }}></span>
            buttonLabel = 'best';
        }
    } else if (singleEpletRisk == Risk.Intermediate) {
        hasData = true;
        singleRisk = 'Medium';
        buttonText = 'med';
        buttonLabel = 'warning';
        singleLabel = 'warning';
    } else if (singleEpletRisk == Risk.High) {
        hasData = true;
        singleRisk = 'High';
        buttonText = 'high';
        buttonLabel = 'danger';
        singleLabel = 'danger';
    }

    if (totalEpletRisk == Risk.Low) {
        hasData = true;
        totalRisk = 'Low';
        totalLabel = 'success';
    }
    else if (totalEpletRisk == Risk.High) {
        hasData = true;
        totalRisk = 'High';
        totalLabel = 'danger';
    }

    if (overallRisk != null && isAboCompatible == false) {
        buttonText = 'ABOi';
        buttonLabel = 'inverse';
    }

    if (expectedWaitTimeLowEmm > 4 && props.showExpectedWaitTimeBlue) {
        buttonLabel = 'primary';
    }

    if (hasData) {
        if (singleEpletMmDq != singleEpletMmDqLow) {
            dqRange = <>{singleEpletMmDqLow}-{singleEpletMmDq} <span style={{ color: 'red' }}>(needs review)</span></>
        } else {
            dqRange = <>{singleEpletMmDq}</>
        }
    }

    if (expectedWaitTimeLowEmm != null) {
        waitText = <div>Expected Low Encounter Wait Time: {expectedWaitTimeLowEmm} Months</div>;
    }

    // const epletLink = __OUTSIDEURL__ + `/${props.isAdmin ? 'admin' : 'centers'}/recipient/eplet-mismatch?id=${props.data.recipientId}&donor=${props.data.donorId}`;

    const epletLinkNew = `/${props.isAdmin ? 'admin' : 'center'}/${props.centerId}/recipient/${props.data.recipientId}/eplet-mm/${props.data.donorId}`;

    const Overlay = hasData ?
        (
            <PopoverWrapper>
                <div style={{ color: 'black' }}>
                    <div style={{ marginBottom: 20 }}>
                        <div><strong>HLA-DR/DQ Single Molecule Mismatch Details</strong></div>
                        <div style={{ marginBottom: 15 }}><span className={`label label-${singleLabel}`}>{singleRisk} Risk</span></div>

                        <div>Top HLA-DR Molecular Mismatch: {props.data.singleEpletMmDr}</div>
                        <div style={{ marginBottom: 15 }}>Top HLA-DQ Molecular Mismatch: {dqRange}</div>
                        {waitText}
                    </div>

                    {ambiAlert}

                    <a className='btn btn-primary' style={{ width: '100%' }} href={epletLinkNew} target='_blank'>See Patient/Donor Eplet Mismatch Report</a>
                </div>
            </PopoverWrapper>
        )
        :
        (
            <PopoverTemplate>
                <div>This donor has not yet been evaluated for the single eplet mismatch risk. This can occur if either the donor or recipient were added today or if they have not yet completed typing.</div>
            </PopoverTemplate>
        );

    return (
        <OverlayTrigger ref={triggerRef} trigger='click' placement={props.placement != null ? props.placement : 'right'} rootClose overlay={Overlay}>
            <Button bsStyle={buttonLabel} className={ambiAppend} bsSize='xs' style={{ height: props.height, width: props.width ? props.width : '100%' }}>{star}{buttonText.toUpperCase()}{star}</Button>
        </OverlayTrigger>
    );
}

export default SingleEpletMmRisk;